import { Component, OnInit, ElementRef } from '@angular/core';
import { PageBase, RentalInfo, NuwielPowerState, NuwielParkBrake } from 't4core';
import { AppIntegrationService } from 't4-app-integration';
import { NavigationService, ActiveRentalService } from 'app-components';
import { SmartLockInfo } from '../../../../../projects/t4core/src/public-api';

type NuwielMode = 'HAND' | 'BIKE';

interface NuwielStep {
  id: string;
  requiresCheck?: boolean;
  showModeSelector?: boolean;
}

@Component({
  selector: 'app-nuwiel-trailer',
  templateUrl: './nuwiel-trailer.component.html',
  styleUrls: ['./nuwiel-trailer.component.css'],
  standalone: false
})
export class NuwielTrailerComponent extends PageBase implements OnInit {
  public booking: RentalInfo;
  public event: string = '';
  public nuwielLock: SmartLockInfo;
  public unlockPin: string;
  public lockInfo: SmartLockInfo;

  public isWaitingPin: boolean = true;
  public hasPinFailed: boolean = false;
  public stepShowPin: boolean = true;
  public failReason: number | null = null;

  public selectedMode: NuwielMode | null = null;
  public currentStepIndex: number = -1;
  public steps: NuwielStep[] = [];

  public isCheckingLockState: boolean = false;
  public hasCheckFailed: boolean = false;
  public checkAttempt: number = 0;
  public maxCheckAttempts: number = 5;

  public powerOnSlides = [];
  public powerOffSlides = [];
  public parkingModeOffSlides = [];
  public parkingModeOnBikeSlides = [];
  public parkingModeOnHandSlides = [];
  public bikeModeSlides = [];
  public handCartModeSlides = [];
  public moveToBikeSlides = [];

  private isChangingStep: boolean = false;

  constructor(
    el: ElementRef,
    public appSvc: AppIntegrationService,
    private navigationService: NavigationService,
    private rentalService: ActiveRentalService
  ) {
    super(el);
    this.appSvc.requestDeviceState();
    this.event = this.navigationService.getParam('event');
  }

  async ngOnInit() {
    this.booking = await this.rentalService.getBooking();
    this.nuwielLock = this.booking?.RentalObject?.Locks?.find(x => x.Type?.toUpperCase() === 'NUWIEL');

    if (this.nuwielLock?.Password) {
      this.unlockPin = this.nuwielLock.Password;
    }

    this.buildFlow();
    this.preloadNuwielImages();
    await this.prepareFirstSlides();
    this.prepareRemainingSliders().catch(error => {
      console.error('Failed to prepare Nuwiel slides', error);
    });

    this.stepShowPin = true;
    this.currentStepIndex = -1;
    this.isWaitingPin = false;
  }


  public get currentStep(): NuwielStep | null {
    if (this.currentStepIndex < 0 || this.currentStepIndex >= this.steps.length) {
      return null;
    }

    return this.steps[this.currentStepIndex] ?? null;
  }

  public get isReturnFlow(): boolean {
    return (this.event || '').toLowerCase() === 'return';
  }

  public get isActiveFlow(): boolean {
    return (this.event || '').toLowerCase() === 'active';
  }

  public get isActiveParkFlow(): boolean {
    return (this.event || '').toLowerCase() === 'active-park';
  }

  public get isPickupFlow(): boolean {
    return !this.isReturnFlow && !this.isActiveFlow && !this.isActiveParkFlow;
  }

  public get returnParkingModeSlides() {
    return this.selectedMode === 'BIKE'
      ? this.parkingModeOnBikeSlides
      : this.parkingModeOnHandSlides;
  }

  public get showPreviousButton(): boolean {
    if (this.isCheckingLockState || this.hasCheckFailed) {
      return false;
    }

    if (this.stepShowPin) {
      return false;
    }

    if (!this.currentStep) {
      return false;
    }
    return true;
  }

  private buildFlow(): void {
    this.selectedMode = null;

    if (this.isActiveParkFlow) {
      this.selectedMode = 'HAND';

      this.steps = [
        { id: 'parking-mode-on' },
        { id: 'power-off' }
      ];

      return;
    }

    if (this.isActiveFlow) {
      this.steps = [
        { id: 'power-on' },
        { id: 'parking-mode-off' },
        { id: 'mode-select', showModeSelector: true },
        { id: 'hand-mode-instructions' },
        { id: 'bike-pin' },
        { id: 'parking-mode-on' },
        { id: 'power-off' }
      ];
      return;
    }

    if (this.isReturnFlow) {
      this.steps = [
        { id: 'parking-mode-select', showModeSelector: true },
        { id: 'parking-mode-on', requiresCheck: true },
        { id: 'power-off', requiresCheck: true }
      ];
      return;
    }

    this.steps = [
      { id: 'power-on', requiresCheck: true },
      { id: 'parking-mode-off', requiresCheck: true },
      { id: 'mode-select', showModeSelector: true },
      { id: 'move-to-bike' },
      { id: 'hand-mode-instructions' },
      { id: 'bike-pin' }
    ];
  }

  public async next(): Promise<void> {
    if (this.isCheckingLockState || this.isChangingStep) {
      return;
    }

    if (this.stepShowPin) {
      this.stepShowPin = false;
      this.currentStepIndex = 0;
      this.scrollToTop();
      return;
    }

    if (!this.currentStep) {
      return;
    }

    if (this.currentStep.id === 'mode-select' || this.currentStep.id === 'parking-mode-select') {
      return;
    }

    if (this.currentStep.requiresCheck) {
      await this.checkCurrentStepStatus();
      return;
    }

    if (this.currentStep.id === 'move-to-bike') {
      this.jumpToStep('bike-pin');
      return;
    }

    if (this.currentStep.id === 'hand-mode-instructions' || this.currentStep.id === 'bike-pin') {
      if (this.isActiveFlow) {
        this.jumpToStep('parking-mode-on');
      } else {
        this.navigateToSecurityCheck();
      }
      return;
    }

    if (this.isActiveFlow || this.isActiveParkFlow) {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.currentStepIndex++;
        this.scrollToTop();
      } else {
        this.goBack();
      }
      return;
    }

    this.goToNextStep();
  }

  public continueNuwiel(): void {
    this.next();
  }

  public previousStep(): void {
    if (this.isCheckingLockState || this.hasCheckFailed || this.isChangingStep) {
      return;
    }

    if (this.stepShowPin) {
      return;
    }

    if (!this.currentStep) {
      return;
    }
    if (this.currentStepIndex === 0) {
      this.stepShowPin = true;
      this.currentStepIndex = -1;
      this.scrollToTop();
      return;
    }

    switch (this.currentStep.id) {
      case 'parking-mode-on':
        if (this.isReturnFlow) {
          this.jumpToStep('parking-mode-select');
          return;
        }

        if (this.isActiveFlow) {
          this.jumpToStep(this.selectedMode === 'BIKE' ? 'bike-pin' : 'hand-mode-instructions');
          return;
        }
        if (this.isActiveParkFlow) {
          this.stepShowPin = true;
          this.currentStepIndex = -1;
          this.scrollToTop();
          return;
        }

        break;

      case 'power-off':
        this.jumpToStep('parking-mode-on');
        return;

      case 'bike-pin':
        if (this.isActiveFlow) {
          this.jumpToStep('mode-select');
        } else {
          this.jumpToStep('move-to-bike');
        }
        return;

      case 'hand-mode-instructions':
        this.jumpToStep('mode-select');
        return;

      case 'move-to-bike':
        this.jumpToStep('mode-select');
        return;

      case 'mode-select':
        this.jumpToStep('parking-mode-off');
        return;

      case 'parking-mode-select':
        if (this.isReturnFlow) {
          this.stepShowPin = true;
          this.currentStepIndex = -1;
          this.scrollToTop();
        } else if (this.isActiveFlow) {
          this.jumpToStep(this.selectedMode === 'BIKE' ? 'bike-pin' : 'hand-mode-instructions');
        }
        return;
    }

    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.scrollToTop();
    }
  }

  public selectMode(mode: NuwielMode): void {
    this.selectedMode = mode;

    if (!this.currentStep || this.currentStep.id !== 'mode-select') {
      return;
    }

    if (mode === 'HAND') {
      this.jumpToStep('hand-mode-instructions');
      return;
    }

    if (mode === 'BIKE') {
      if (this.isActiveFlow) {
        this.jumpToStep('bike-pin');
      } else {
        this.jumpToStep('move-to-bike');
      }
    }
  }

  public selectParkingModeUsed(mode: NuwielMode): void {
    this.selectedMode = mode;
    this.jumpToStep('parking-mode-on');
  }

  public canContinue(): boolean {
    if (this.isCheckingLockState) {
      return false;
    }

    if (this.stepShowPin) {
      return true;
    }

    if (!this.currentStep) {
      return false;
    }

    if (this.currentStep.id === 'mode-select' || this.currentStep.id === 'parking-mode-select') {
      return false;
    }

    return true;
  }

  public async retry(): Promise<void> {
    if (this.stepShowPin) {
      this.hasPinFailed = false;
      this.failReason = null;
      return;
    }

    if (this.hasCheckFailed) {
      this.hasCheckFailed = false;
      await this.checkCurrentStepStatus();
      return;
    }

    this.hasPinFailed = false;
    this.failReason = null;
    this.isCheckingLockState = false;
  }

  public continueAfterCheck(): void {
    if ((!this.isCheckingLockState && !this.hasCheckFailed) || !this.currentStep?.requiresCheck) {
      return;
    }

    this.hasCheckFailed = false;
    this.isCheckingLockState = false;
    this.handleCompletedStepSafely(this.currentStep.id);
  }

  public getManualContinueText(): string {
    switch (this.currentStep?.id) {
      case 'power-on':
        return 'Power is on';
      case 'parking-mode-off':
        return 'Brake is released';
      case 'parking-mode-on':
        return 'Brake is applied';
      case 'power-off':
        return 'Power is off';
      default:
        return 'Continue';
    }
  }


  private async checkCurrentStepStatus(): Promise<void> {
    if (!this.currentStep) {
      return;
    }

    this.hasCheckFailed = false;
    this.isCheckingLockState = true;
    this.checkAttempt = 0;
    this.scrollToTop();

    const stepId = this.currentStep.id;

    for (let attempt = 1; attempt <= this.maxCheckAttempts; attempt++) {
      this.checkAttempt = attempt;

      try {
        await this.getLockInfo();

        if (!this.currentStep || this.currentStep.id !== stepId) {
          return;
        }

        if (this.isStepCompleted(stepId)) {
          this.isCheckingLockState = false;
          this.hasCheckFailed = false;
          this.handleCompletedStepSafely(stepId);
          return;
        }
      } catch {
        // Continue retrying
      }

      if (attempt < this.maxCheckAttempts) {
        await this.delay(2000);
      }
    }

    this.isCheckingLockState = false;
    this.hasCheckFailed = true;
  }

  private isStepCompleted(stepId: string): boolean {
    switch (stepId) {
      case 'power-on':
        return this.lockInfo?.PowerState === NuwielPowerState.On;

      case 'parking-mode-off':
        return this.lockInfo?.ParkBrake === NuwielParkBrake.Released;

      case 'parking-mode-on':
        return this.lockInfo?.ParkBrake === NuwielParkBrake.AppliedByButton;

      case 'power-off':
        return this.lockInfo?.PowerState === NuwielPowerState.Off;

      default:
        return true;
    }
  }

  private handleCompletedStepSafely(stepId: string): void {
    if (this.isChangingStep) {
      return;
    }

    if (!this.currentStep || this.currentStep.id !== stepId) {
      return;
    }

    this.isChangingStep = true;

    try {
      this.handleCompletedStep(stepId);
    } finally {
      setTimeout(() => {
        this.isChangingStep = false;
      }, 300);
    }
  }

  private handleCompletedStep(stepId: string): void {
    if (stepId === 'power-off') {
      if (this.isReturnFlow) {
        this.navigateToSecurityCheck();
        return;
      }

      if (this.isActiveFlow || this.isActiveParkFlow) {
        this.goBack();
        return;
      }
    }

    this.goToNextStep();
  }

  private jumpToStep(stepId: string): void {
    const index = this.steps.findIndex(x => x.id === stepId);

    if (index >= 0) {
      this.currentStepIndex = index;
      this.scrollToTop();
    }
  }

  private goToNextStep(): void {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
      this.scrollToTop();
      return;
    }

    if (this.isActiveFlow || this.isActiveParkFlow) {
      this.goBack();
      return;
    }

    this.finishFlow();
  }

  private navigateToSecurityCheck(): void {
    this.navigationService.executeCommand('Next');
  }

  private goBack(): void {
    this.navigationService.executeCommand('Inflight');
  }

  public troubleshootNuwiel(): void {
    this.fail(3);
  }

  private fail(reason?: number): void {
    if (this.hasPinFailed) {
      return;
    }

    this.hasPinFailed = true;

    if (reason != null) {
      this.failReason = reason;
    }
  }

  public async getLockInfo(): Promise<void> {
    this.lockInfo = await this.Api.get<SmartLockInfo>('/RentalObject/GetLockInfo', {
      lockId: this.nuwielLock?.Id
    });
  }

  public skip(): void {
    this.finishFlow();
  }

  private finishFlow(): void {
    this.isCheckingLockState = false;
    this.hasCheckFailed = false;
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private preloadNuwielImages(): void {
    const imageUrls = [
      '/Images/Nuwiel/nuwiel-lock-box.png',
      '/Images/Nuwiel/remote-nuwiel.webp',
      '/Images/Nuwiel/park-off-nuwiel.webp',
      '/Images/Nuwiel/park-mode-button.webp',
      '/Images/Nuwiel/nuwiel-park-bike-step1.webp',
      '/Images/Nuwiel/nuwiel-park-bike-step2.webp',
      '/Images/Nuwiel/nuwiel-park-bike-step3.webp',
      '/Images/Nuwiel/nuwiel-park-hand-step1.webp',
      '/Images/Nuwiel/nuwiel-park-hand-step2.webp',
      '/Images/Nuwiel/nuwiel-park-hand-step3.webp',
      '/Images/Nuwiel/nuwiel-bike-step1.webp',
      '/Images/Nuwiel/nuwiel-bike-step2a.webp',
      '/Images/Nuwiel/nuwiel-bike-step2b.webp',
      '/Images/Nuwiel/nuwiel-bike-step3.webp',
      '/Images/Nuwiel/nuwiel-bike-step4.webp',
      '/Images/Nuwiel/nuwiel-bike-step5.webp',
      '/Images/Nuwiel/nuwiel-hand-step1.webp',
      '/Images/Nuwiel/nuwiel-hand-step2.webp',
      '/Images/Nuwiel/nuwiel-hand-step3.webp',
      '/Images/Nuwiel/move-to-bike.webp',
      '/Images/Nuwiel/nuwiel-bike-mode.jpg',
      '/Images/Nuwiel/nuwiel-hand-mode.jpg'
    ];

    imageUrls.forEach(url => {
      const image = new Image();
      image.src = url;
    });
  }

  private async prepareFirstSlides(): Promise<void> {
    this.powerOnSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.SwitchOn.Title1', 'Turn on the eTrailer'),
        image: '/Images/Nuwiel/remote-nuwiel.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.SwitchOn1.MessageStep1', 'Press the top button on the key fob until it beeps and the lights turn on. The eTrailer is now activated.')
      }
    ];

    this.parkingModeOffSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ReleaseParkingMode1.TitleStep1', 'Position the towbar'),
        image: '/Images/Nuwiel/park-off-nuwiel.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ReleaseParkingMode1.MessageStep1', 'Fold the towbar down and hold it comfortably at your side')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ReleaseParkingMode1.TitleStep2', 'Release parking brakes'),
        image: '/Images/Nuwiel/park-mode-button.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ReleaseParkingMode1.MessageStep2', 'Press and hold the eBrake button for 1 second, until you hear a sound. The button colour will change from red to neutral.')
      }
    ];
  }

  private async prepareRemainingSliders() {   
    this.parkingModeOnBikeSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.TitleStep1', 'Remove the safety pin'),
        image: '/Images/Nuwiel/nuwiel-park-bike-step1.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.MessageStep1', 'Open the pin-latch and remove the safety pin from the towbar.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.TitleStep2', 'Unwrap the safety strap'),
        image: '/Images/Nuwiel/nuwiel-park-bike-step2.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.MessageStep2', 'Unwrap the safety strap from the seat post.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.TitleStep3', 'Remove the coupling'),
        image: '/Images/Nuwiel/nuwiel-park-bike-step3.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.MessageStep3', 'Remove the coupling from the seat post and stow it securely on the eTrailer.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.TitleStep4', 'Stow the safety strap'),
        image: '/Images/Nuwiel/nuwiel-park-hand-step1.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.MessageStep4', 'Wrap the safety strap around the coupling, push the safety pin through, and secure the pin-latch.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.TitleStep5', 'Engage the parking brake'),
        image: '/Images/Nuwiel/nuwiel-park-hand-step2.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.MessageStep5', 'Press and hold the eBrake button for 1 second until it beeps. The button will light up red.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.TitleStep6', 'Position the towbar'),
        image: '/Images/Nuwiel/nuwiel-park-hand-step3.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Bike.MessageStep6', 'Fold the towbar all the way up.')
      }
    ];

    this.parkingModeOnHandSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Hand.TitleStep1', 'Stow the safety strap'),
        image: '/Images/Nuwiel/nuwiel-park-hand-step1.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Hand.MessageStep1', 'Wrap the safety strap around the coupling, push the safety pin through, and secure the pin-latch.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Hand.TitleStep2', 'Engage the parking brake'),
        image: '/Images/Nuwiel/nuwiel-park-hand-step2.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Hand.MessageStep2', 'Press and hold the eBrake button for 1 second until it beeps. The button will light up red.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Hand.TitleStep3', 'Position the towbar'),
        image: '/Images/Nuwiel/nuwiel-park-hand-step3.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.ApplyParkingMode.Hand.MessageStep3', 'Fold the towbar all the way up.')
      }
    ];

    this.bikeModeSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.TitleStep1', 'Prepare the coupling'),
        image: '/Images/Nuwiel/nuwiel-bike-step1.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.MessageStep1', 'Remove the safety strap from the coupling.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.TitleStep2a', 'Mount the coupling'),
        image: '/Images/Nuwiel/nuwiel-bike-step2a.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.MessageStep2a', 'Attach, align, and tighten the coupling.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.TitleStep2b', 'Align the Towbar'),
        image: '/Images/Nuwiel/nuwiel-bike-step2b.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.MessageStep2b', 'Make sure the towbar is straight and the coupling clamp is parallel to the seat post.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.TitleStep3', 'Secure the safety strap'),
        image: '/Images/Nuwiel/nuwiel-bike-step3.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.MessageStep3', 'Wrap the safety strap twice around the seat post.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.TitleStep4', 'Insert the safety pin'),
        image: '/Images/Nuwiel/nuwiel-bike-step4.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.MessageStep4', 'Push the locking pin through the hole at the end of the towbar. Secure the pin-latch over the towbar.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.TitleStep5', 'Ride off gently'),
        image: '/Images/Nuwiel/nuwiel-bike-step5.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.BikeMode1.MessageStep5', 'Ride off gently and take corners slowly. The eTrailer weighs 90+ kg and is 96cm wide.')
      }
    ];

    this.handCartModeSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.HandCartMode1.TitleStep1', 'Secure the safety strap'),
        image: '/Images/Nuwiel/nuwiel-hand-step1.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.HandCartMode1.MessageStep1', 'If not already done, wrap the safety strap around the coupling.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.HandCartMode1.TitleStep2', 'Center the slider'),
        image: '/Images/Nuwiel/nuwiel-hand-step2.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.HandCartMode1.MessageStep2', 'Gently move the towbar back and forth until the slider is in the middle.')
      },
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.HandCartMode1.TitleStep3', 'Start walking gently'),
        image: '/Images/Nuwiel/nuwiel-hand-step3.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.HandCartMode1.MessageStep3', 'Hold the handle naturally at your side. The eTrailer brakes automatically if the towbar is too high or too low.')
      }
    ];

    this.moveToBikeSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.MoveToBike1.Title', 'Bring the eTrailer to your bike'),
        image: '/Images/Nuwiel/move-to-bike.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.MoveToBike1.MessageStep', 'Position the eTrailer behind your bike before activating cycling mode.')
      }
    ];

    this.powerOffSlides = [
      {
        title: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.SwitchOff1.Title1', 'Turn off the eTrailer'),
        image: '/Images/Nuwiel/remote-nuwiel.webp',
        text: await this.Translate.translate('Intellitrailer.Rental.Nuwiel.Step.SwitchOff1.MessageStep1', 'Press the top button on the key fob to turn off the eTrailer. Place the key fob inside the lockbox and close it securely.')
      }
    ];
  }
}
